import { Component, computed, input, output } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatOptionSelectionChange, MatPseudoCheckboxState } from '@angular/material/core';

export interface NestedSelectOption<T = string> {
	parent: Omit<NestedSelectOption<T>, 'parent'>;
	value: T;
	display: string;
}

@Component({
	selector: 'dix-nested-select',
	standalone: false,
	templateUrl: './nested-select.component.html',
})
export class NestedSelectComponent {
	options = input.required<NestedSelectOption[]>();

	value = input<string[]>([]);
	fieldClass = input<string>();

	formCtrl = input<AbstractControl<string[] | null> | null>(null);

	title = input.required<string>();
	emptyText = input.required<string>();

	selectionChange = output<string[]>();

	childrenValues = computed(() => this.options().map((c) => c.value));
	parents = computed(() =>
		uniqueBy(
			this.options().map((item) => item.parent),
			(p) => p.value,
		),
	);
	itemsByParent = computed(() => Object.groupBy(this.options(), (item) => item.parent.value));

	selectedValues = computed(() => {
		this.currentValue();
	});

	currentValue(): string[] {
		const fc = this.formCtrl();
		const raw = fc?.value ?? this.value();
		return raw ?? [];
	}

	private updateSelection(newSelected: string[]): void {
		const fc = this.formCtrl();
		if (fc) {
			fc.setValue(newSelected);
			fc.markAsDirty();
			fc.markAsTouched();
		} else {
			this.selectionChange.emit(newSelected);
		}
	}

	onSelectionChange(values: string[]) {
		const newSelected = values ?? [];
		this.updateSelection(newSelected);
	}

	onAllToggle(ev: MatOptionSelectionChange) {
		if (!ev.isUserInput) return;
		ev.source.deselect();

		const ids = new Set(this.options().map((l) => l.value));
		this.toggleGroup(ids);
	}

	onTypeToggle(parent: string, ev: MatOptionSelectionChange) {
		if (!ev.isUserInput) return;
		ev.source.deselect();

		const ids = new Set(this.itemsByParent()[parent].map((l) => l.value));
		this.toggleGroup(ids);
	}

	private toggleGroup(ids: Set<string>): void {
		const current = new Set(this.currentValue());

		const allSelected = ids.isSubsetOf(current);
		const newSelected = allSelected ? current.difference(ids) : ids.union(current);

		this.updateSelection(Array.from(newSelected));
	}

	private groupCheckboxState(ids: string[], selected: Set<string>): MatPseudoCheckboxState {
		if (ids.length === 0) return 'unchecked';

		let selectedCount = 0;
		for (const id of ids) if (selected.has(id)) selectedCount++;

		if (selectedCount === 0) return 'unchecked';
		if (selectedCount === ids.length) return 'checked';
		return 'indeterminate';
	}

	typeCheckboxState(type: string): MatPseudoCheckboxState {
		const selected = new Set(this.currentValue());
		const ids = this.itemsByParent()[type].map((l) => l.value);
		return this.groupCheckboxState(ids, selected);
	}

	selectAllCheckboxState(): MatPseudoCheckboxState {
		const selected = new Set(this.currentValue());
		const ids = this.options().map((l) => l.value);
		return this.groupCheckboxState(ids, selected);
	}
}

function uniqueBy<T>(arr: T[], key: (a: T) => T[keyof T]) {
	const seen: Record<string, boolean> = {};
	return arr.filter((item) => {
		const k = key(item) as string;
		return Object.hasOwn(seen, k) ? false : (seen[k] = true);
	});
}
